import { useEffect, useState, useRef } from 'react';
import request from '../../utils/request';
import './EditFunctionModal.css';

function isInsideModalContent(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('.modal-content') !== null;
}

interface FunctionItem {
  id: number;
  name: string;
  code: string;
  sort: number;
  status: number;
  created_at: string;
  updated_at: string;
}

interface EditFunctionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  functionData: FunctionItem | null;
}

interface UpdateFunctionRequest {
  name: string;
  sort: number;
}

export default function EditFunctionModal({
  isOpen,
  onClose,
  onSuccess,
  functionData,
}: EditFunctionModalProps) {
  const [name, setName] = useState('');
  const [sort, setSort] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);

  useEffect(() => {
    if (functionData) {
      setName(functionData.name);
      setSort(functionData.sort);
    }
  }, [functionData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter function name');
      return;
    }

    if (!functionData) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const data: UpdateFunctionRequest = {
        name: name.trim(),
        sort: sort,
      };

      await request.put(`/gradmin/functions/${functionData.id}`, data);

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  if (!isOpen || !functionData) return null;

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={e => {
        mouseDownInsideContent.current = isInsideModalContent(e.target);
      }}
      onClick={e => {
        if (e.target === overlayRef.current && !mouseDownInsideContent.current) {
          handleClose();
        }
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Edit Function</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error-alert">{error}</div>}

            <div className="modal-form-group">
              <label htmlFor="edit-function-name" className="modal-form-label">
                Function Name <span className="modal-required">*</span>
              </label>
              <input
                id="edit-function-name"
                type="text"
                className="modal-form-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter function name"
                disabled={loading}
              />
            </div>

            <div className="modal-form-group">
              <label htmlFor="edit-function-sort" className="modal-form-label">
                Sort
              </label>
              <input
                id="edit-function-sort"
                type="number"
                className="modal-form-input"
                value={sort}
                onChange={e => setSort(Number(e.target.value))}
                placeholder="Enter sort value (smaller = higher priority)"
                disabled={loading}
              />
              <span className="modal-form-hint">Smaller value means higher priority.</span>
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label">Function Code</label>
              <input type="text" className="modal-form-input" value={functionData.code} disabled />
              <span className="modal-form-hint">Function code cannot be modified</span>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="modal-btn modal-btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="modal-btn modal-btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
